import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { Button } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { RelatorioContainer } from '@/shared/components/relatorios/components/relatorio-container/relatorio-container';
import { RelatorioMedicoBase } from '@/features/registro/medicos/relatorios/relatorio-medico.base';
import { RelatorioJobFacade } from '@/shared/components/relatorios/relatorio-job-base';
import { Pesquisa } from '@/shared/components/pessoa/pesquisa/pesquisa';
import { CardMedicoSelecionado } from '@/features/registro/medicos/relatorios/components/card-medico-selecionado/card-medico-selecionado';
import { RequerimentoUnicoMedicoService } from '@/core/api/sicom-api/registro/medico/relatorios/requerimento-unico-medico/requerimento-unico-medico.service';
import { ReportRequerimentoUnicoMedicoRequest, ReportRequerimentoUnicoMedicoResponse } from '@/core/api/sicom-api/registro/medico/relatorios/requerimento-unico-medico/requerimento-unico-medico.model';
import { RequerimentoUnicoMedicoJobService } from '@/core/services/requerimento-unico-medico-job.service';
import { CanDirective } from '@/core/permissions/can.directive';

type Req = ReportRequerimentoUnicoMedicoRequest;
type Resp = ReportRequerimentoUnicoMedicoResponse;

@Component({
    selector: 'app-requerimento-unico-medico',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, Button, TooltipModule, RelatorioContainer, Pesquisa, CardMedicoSelecionado, CanDirective],
    templateUrl: './requerimento-unico-medico.html',
    styleUrl: './requerimento-unico-medico.scss'
})
/**
 * Relatório PDF assíncrono do Requerimento Único do médico (nuCrm/sgUf).
 * Herda `RelatorioMedicoBase`: toda a orquestração de export/polling/download/erro e o card
 * "médico selecionado + Alterar" vêm da base; aqui só resolvemos a identificação do médico e
 * delegamos o HTTP ao service.
 */
export class ReportRequerimentoUnicoMedicoComponent extends RelatorioMedicoBase<Req, Resp> {
    private readonly requerimentoService = inject(RequerimentoUnicoMedicoService);
    readonly requerimentoJobService = inject(RequerimentoUnicoMedicoJobService);

    /** Stream do job para o `app-relatorio-container` (preview/PDF) no template. */
    readonly job$ = this.requerimentoJobService.job$;

    protected get jobFacade(): RelatorioJobFacade {
        return this.requerimentoJobService;
    }

    /** Monta o request a partir da pessoa selecionada; retorna null (e avisa) se faltar CRM/UF. */
    protected buildRequest(): Req | null {
        const medico = this.resolverIdentificacaoMedico('Selecione um médico (CRM/UF) antes de gerar o requerimento.');
        if (!medico) return null;
        const { nuCrm, sgUf } = medico;

        return { nuCrm, sgUf };
    }

    protected exportar(request: Req) {
        return this.requerimentoService.exportarPdf(request);
    }

    /** Limpa o job/preview corrente (o requerimento não tem formulário de dados a resetar). */
    limparFormulario(): void {
        this.limparJob();
    }
}
